import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  Grid,
  Paper,
  TextField,
  Button,
  Divider
} from '@mui/material';
import {
  DragIndicator as DragIndicatorIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { HVLT_R_WORD_LIST } from '../types/test';

interface HVLTRInputProps {
  ensayo1Words: string[];
  ensayo2Words: string[];
  ensayo3Words: string[];
  ensayo4Words: string[];
  onEnsayo1WordsChange: (words: string[]) => void;
  onEnsayo2WordsChange: (words: string[]) => void;
  onEnsayo3WordsChange: (words: string[]) => void;
  onEnsayo4WordsChange: (words: string[]) => void;
  onEnsayo1ScoreChange: (score: number) => void;
  onEnsayo2ScoreChange: (score: number) => void;
  onEnsayo3ScoreChange: (score: number) => void;
  onEnsayo4ScoreChange: (score: number) => void;
  ensayo1Score: number;
  ensayo2Score: number;
  ensayo3Score: number;
  ensayo4Score: number;
}

const HVLTRInput: React.FC<HVLTRInputProps> = ({
  ensayo1Words,
  ensayo2Words,
  ensayo3Words,
  ensayo4Words,
  onEnsayo1WordsChange,
  onEnsayo2WordsChange,
  onEnsayo3WordsChange,
  onEnsayo4WordsChange,
  onEnsayo1ScoreChange,
  onEnsayo2ScoreChange,
  onEnsayo3ScoreChange,
  onEnsayo4ScoreChange,
  ensayo1Score,
  ensayo2Score,
  ensayo3Score,
  ensayo4Score
}) => {
  const [activeColumn, setActiveColumn] = useState<'ensayo1' | 'ensayo2' | 'ensayo3' | 'ensayo4' | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<{ column: string; index: number } | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<{ column: string; index: number } | null>(null);

  // Altura fija para el área de palabras registradas: suficiente para 12 palabras sin scroll
  // Cada chip ~32px altura + gap 4px, 12 palabras = 12*32 + 11*4 = 384 + 44 = 428px + padding
  // Ajustado a 480px para que el área de palabras (480-120=360px) quepa las 12 palabras sin espacio vacío
  const FIXED_HEIGHT = 480;

  const handleColumnClick = (column: 'ensayo1' | 'ensayo2' | 'ensayo3' | 'ensayo4') => {
    setActiveColumn(activeColumn === column ? null : column);
  };

  const handleWordClick = (word: string) => {
    if (!activeColumn) return;

    const columnMap = {
      ensayo1: { words: ensayo1Words, onChange: onEnsayo1WordsChange, onScoreChange: onEnsayo1ScoreChange },
      ensayo2: { words: ensayo2Words, onChange: onEnsayo2WordsChange, onScoreChange: onEnsayo2ScoreChange },
      ensayo3: { words: ensayo3Words, onChange: onEnsayo3WordsChange, onScoreChange: onEnsayo3ScoreChange },
      ensayo4: { words: ensayo4Words, onChange: onEnsayo4WordsChange, onScoreChange: onEnsayo4ScoreChange }
    };

    const column = columnMap[activeColumn];
    
    if (column.words.includes(word)) {
      // Remover palabra
      const newWords = column.words.filter(w => w !== word);
      column.onChange(newWords);
      column.onScoreChange(newWords.length);
    } else {
      // Agregar palabra al final
      const newWords = [...column.words, word];
      column.onChange(newWords);
      column.onScoreChange(newWords.length);
    }
  };

  const handleRemoveWord = (word: string, column: 'ensayo1' | 'ensayo2' | 'ensayo3' | 'ensayo4') => {
    const columnMap = {
      ensayo1: { words: ensayo1Words, onChange: onEnsayo1WordsChange, onScoreChange: onEnsayo1ScoreChange },
      ensayo2: { words: ensayo2Words, onChange: onEnsayo2WordsChange, onScoreChange: onEnsayo2ScoreChange },
      ensayo3: { words: ensayo3Words, onChange: onEnsayo3WordsChange, onScoreChange: onEnsayo3ScoreChange },
      ensayo4: { words: ensayo4Words, onChange: onEnsayo4WordsChange, onScoreChange: onEnsayo4ScoreChange }
    };

    const col = columnMap[column];
    const newWords = col.words.filter(w => w !== word);
    col.onChange(newWords);
    col.onScoreChange(newWords.length);
  };

  // Drag and Drop handlers
  const handleDragStart = (e: React.DragEvent, column: string, index: number) => {
    setDraggedIndex({ column, index });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, column: string, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex({ column, index });
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, targetColumn: string, targetIndex: number) => {
    e.preventDefault();
    
    if (!draggedIndex || draggedIndex.column !== targetColumn || draggedIndex.index === targetIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const columnMap = {
      ensayo1: { words: ensayo1Words, onChange: onEnsayo1WordsChange },
      ensayo2: { words: ensayo2Words, onChange: onEnsayo2WordsChange },
      ensayo3: { words: ensayo3Words, onChange: onEnsayo3WordsChange },
      ensayo4: { words: ensayo4Words, onChange: onEnsayo4WordsChange }
    };

    const col = columnMap[targetColumn as keyof typeof columnMap];
    const newWords = [...col.words];
    const draggedWord = newWords[draggedIndex.index];
    
    newWords.splice(draggedIndex.index, 1);
    newWords.splice(targetIndex, 0, draggedWord);
    
    col.onChange(newWords);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleManualScoreChange = (column: 'ensayo1' | 'ensayo2' | 'ensayo3' | 'ensayo4', value: string) => {
    const numValue = parseInt(value) || 0;
    if (numValue >= 0 && numValue <= 12) {
      const columnMap = {
        ensayo1: { onChange: onEnsayo1WordsChange, onScoreChange: onEnsayo1ScoreChange },
        ensayo2: { onChange: onEnsayo2WordsChange, onScoreChange: onEnsayo2ScoreChange },
        ensayo3: { onChange: onEnsayo3WordsChange, onScoreChange: onEnsayo3ScoreChange },
        ensayo4: { onChange: onEnsayo4WordsChange, onScoreChange: onEnsayo4ScoreChange }
      };

      const col = columnMap[column];
      col.onScoreChange(numValue);
      
      // Si el número es menor que las palabras actuales, truncar
      const currentWords = {
        ensayo1: ensayo1Words,
        ensayo2: ensayo2Words,
        ensayo3: ensayo3Words,
        ensayo4: ensayo4Words
      }[column];

      if (numValue < currentWords.length) {
        col.onChange(currentWords.slice(0, numValue));
      }
    }
  };

  const getColumnData = (column: 'ensayo1' | 'ensayo2' | 'ensayo3' | 'ensayo4') => {
    const data = {
      ensayo1: { words: ensayo1Words, score: ensayo1Score, label: 'Ensayo 1' },
      ensayo2: { words: ensayo2Words, score: ensayo2Score, label: 'Ensayo 2' },
      ensayo3: { words: ensayo3Words, score: ensayo3Score, label: 'Ensayo 3' },
      ensayo4: { words: ensayo4Words, score: ensayo4Score, label: 'Ensayo 4 (Recuerdo Tardío)' }
    };
    return data[column];
  };

  // Mostrar solo las palabras del ensayo activo
  const isWordInActiveColumn = (word: string) => {
    if (!activeColumn) return false;
    
    const columnMap = {
      ensayo1: ensayo1Words,
      ensayo2: ensayo2Words,
      ensayo3: ensayo3Words,
      ensayo4: ensayo4Words
    };
    
    return columnMap[activeColumn].includes(word);
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
          Hopkins Verbal Learning Test - Revised
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
          Haz clic en una columna para activarla, luego selecciona las palabras que el paciente dijo.
        </Typography>

        {/* Palabras seleccionables - 6 arriba, 6 abajo, alineadas con ancho igual */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
            Palabras disponibles (haz clic en una columna primero):
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* Primera fila: 6 palabras */}
            <Grid container spacing={1.5} sx={{ justifyContent: 'center' }}>
              {HVLT_R_WORD_LIST.slice(0, 6).map((word) => {
                const isSelected = isWordInActiveColumn(word);
                const isDisabled = !activeColumn;
                
                return (
                  <Grid size={{ xs: 6, sm: 4, md: 2 }} key={word} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Chip
                      label={word.charAt(0).toUpperCase() + word.slice(1)}
                      onClick={() => !isDisabled && handleWordClick(word)}
                      color={isSelected ? 'primary' : 'default'}
                      variant={isSelected ? 'filled' : 'outlined'}
                      disabled={isDisabled}
                      sx={{
                        width: '100%',
                        minWidth: 120,
                        maxWidth: 150,
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        opacity: isDisabled ? 0.5 : 1,
                        justifyContent: 'center',
                        ...(isSelected && {
                          backgroundColor: 'primary.main',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'primary.dark'
                          }
                        })
                      }}
                    />
                  </Grid>
                );
              })}
            </Grid>
            {/* Segunda fila: 6 palabras */}
            <Grid container spacing={1.5} sx={{ justifyContent: 'center' }}>
              {HVLT_R_WORD_LIST.slice(6, 12).map((word) => {
                const isSelected = isWordInActiveColumn(word);
                const isDisabled = !activeColumn;
                
                return (
                  <Grid size={{ xs: 6, sm: 4, md: 2 }} key={word} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Chip
                      label={word.charAt(0).toUpperCase() + word.slice(1)}
                      onClick={() => !isDisabled && handleWordClick(word)}
                      color={isSelected ? 'primary' : 'default'}
                      variant={isSelected ? 'filled' : 'outlined'}
                      disabled={isDisabled}
                      sx={{
                        width: '100%',
                        minWidth: 120,
                        maxWidth: 150,
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        opacity: isDisabled ? 0.5 : 1,
                        justifyContent: 'center',
                        ...(isSelected && {
                          backgroundColor: 'primary.main',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'primary.dark'
                          }
                        })
                      }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* 4 Columnas con palabras registradas - Altura fija para 12 palabras sin scroll */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
            Palabras registradas por ensayo:
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
            {(['ensayo1', 'ensayo2', 'ensayo3', 'ensayo4'] as const).map((column) => {
              const columnData = getColumnData(column);
              const isActive = activeColumn === column;
              
              return (
                <Box key={column} sx={{ flex: '1 1 0', minWidth: 0, display: 'flex' }}>
                  <Paper
                    elevation={isActive ? 4 : 1}
                    sx={{
                      p: 2,
                      height: FIXED_HEIGHT,
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      border: isActive ? '3px solid' : '1px solid',
                      borderColor: isActive ? 'primary.main' : 'divider',
                      backgroundColor: isActive ? 'primary.50' : 'background.paper',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: 'primary.main',
                        boxShadow: 3
                      }
                    }}
                    onClick={() => handleColumnClick(column)}
                  >
                    {/* Header de la columna */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography 
                        variant="subtitle2" 
                        fontWeight="bold"
                        color={isActive ? 'primary.main' : 'text.primary'}
                      >
                        {columnData.label}
                      </Typography>
                      {isActive && (
                        <CheckCircleIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                      )}
                    </Box>

                    {/* Área de palabras - altura fija para 12 palabras sin scroll */}
                    <Box
                      sx={{
                        flex: 1,
                        overflowY: 'hidden',
                        minHeight: 0,
                        p: 1,
                        backgroundColor: 'grey.50',
                        borderRadius: 1,
                        mb: 1,
                        height: FIXED_HEIGHT - 120 // Restar espacio del header y puntaje (360px para 12 palabras, ajustado)
                      }}
                    >
                      {columnData.words.length === 0 ? (
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          {isActive ? 'Selecciona palabras arriba' : 'Haz clic para activar'}
                        </Typography>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {columnData.words.map((word, index) => (
                            <Chip
                              key={`${word}-${index}`}
                              label={`${index + 1}. ${word.charAt(0).toUpperCase() + word.slice(1)}`}
                              size="small"
                              onDelete={() => handleRemoveWord(word, column)}
                              icon={<DragIndicatorIcon sx={{ fontSize: 14 }} />}
                              draggable
                              onDragStart={(e) => handleDragStart(e, column, index)}
                              onDragOver={(e) => handleDragOver(e, column, index)}
                              onDragLeave={handleDragLeave}
                              onDrop={(e) => handleDrop(e, column, index)}
                              onDragEnd={handleDragEnd}
                              sx={{
                                cursor: 'move',
                                opacity: draggedIndex?.column === column && draggedIndex?.index === index ? 0.5 : 1,
                                border: dragOverIndex?.column === column && dragOverIndex?.index === index ? '2px dashed' : 'none',
                                borderColor: dragOverIndex?.column === column && dragOverIndex?.index === index ? 'primary.main' : 'transparent',
                                justifyContent: 'flex-start',
                                '&:hover': {
                                  backgroundColor: 'primary.light',
                                  color: 'white'
                                }
                              }}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>

                    {/* Puntaje debajo de cada columna */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <TextField
                        type="number"
                        size="small"
                        value={columnData.score}
                        onChange={(e) => handleManualScoreChange(column, e.target.value)}
                        inputProps={{ min: 0, max: 12 }}
                        sx={{ width: 80 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        / 12
                      </Typography>
                    </Box>
                  </Paper>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Información adicional */}
        <Box sx={{ mt: 2, p: 1.5, backgroundColor: 'info.50', borderRadius: 1 }}>
          <Typography variant="caption" color="info.main">
            <strong>Instrucciones:</strong> 1) Haz clic en una columna para activarla. 2) Selecciona las palabras que el paciente dijo. 
            3) Puedes reordenar arrastrando los chips. 4) También puedes escribir el número directamente en el campo de puntaje.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default HVLTRInput;
